// src/components/layout/Navbar.jsx
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileWhatWeDoOpen, setMobileWhatWeDoOpen] = useState(false);

  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (path) =>
    `navbar-link ${isActive(path) ? 'active' : ''}`;

  const aboutLinks = [
    { href: '/about/who-we-are', label: 'Who We Are' },
    { href: '/about/values', label: 'Our Values' },
    { href: '/about/mission-vision', label: 'Mission & Vision' },
    { href: '/about/team', label: 'Our Team' },

    // ✅ NEW
    { href: '/about/gallery', label: 'Gallery' },
  ];

  const whatWeDoLinks = [
    { href: '/what-we-do', label: 'Overview' },
    { href: '/projects', label: 'Projects' },
    { href: '/tatl1000', label: 'Success Stories' },
  ];

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrolled(currentScrollY > 10);

      // Hide when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileAboutOpen(false);
    setMobileWhatWeDoOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`navbar-premium ${showNavbar ? 'navbar-visible' : 'navbar-hidden'} ${
          isScrolled ? 'navbar-scrolled' : ''
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
          <div className="flex items-center justify-between h-[78px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
              <div className="logo-wrap">
                <img
                  src="/assets/logo.png"
                  alt="GLGIF Logo"
                  className="logo-img"
                />
              </div>
              <div className="leading-tight">
                <span className="block font-heading text-[1.05rem] sm:text-[1.2rem] font-bold text-stone-900">
                  GLGIF
                </span>
                <span className="hidden md:block text-[11px] text-stone-500 tracking-wide">
                  Saving Lives, Redirecting Destinies
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-7">
              <Link to="/" className={navLinkClass('/')} data-testid="nav-home">
                Home
              </Link>

              {/* About Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={`navbar-link inline-flex items-center gap-1.5 ${
                    location.pathname.startsWith('/about') ? 'active' : ''
                  }`}
                  data-testid="nav-about-trigger"
                >
                  About <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="nav-dropdown">
                  {aboutLinks.map((link) => (
                    <DropdownMenuItem key={link.href} asChild>
                      <Link
                        to={link.href}
                        className="nav-dropdown-item"
                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* What We Do Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={`navbar-link inline-flex items-center gap-1.5 ${
                    location.pathname.startsWith('/what-we-do') || location.pathname.startsWith('/projects')
                      ? 'active'
                      : ''
                  }`}
                  data-testid="nav-whatwedo-trigger"
                >
                  What We Do <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="nav-dropdown">
                  {whatWeDoLinks.map((link) => (
                    <DropdownMenuItem key={link.href} asChild>
                      <Link
                        to={link.href}
                        className="nav-dropdown-item"
                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

            
              <Link to="/get-involved" className={navLinkClass('/get-involved')} data-testid="nav-get-involved">
                Get Involved
              </Link>

              <Link to="/blog" className={navLinkClass('/blog')} data-testid="nav-blog">
                Blog
              </Link>

              <Link to="/events" className={navLinkClass('/events')} data-testid="nav-events">
                Events
              </Link>

              <Link to="/contact" className={navLinkClass('/contact')} data-testid="nav-contact">
                Contact
              </Link>
            </div>

            {/* CTA + Mobile Trigger */}
            <div className="flex items-center gap-3">
              <div className="hidden lg:block">
                <Button asChild className="rounded-full px-6 py-2.5 text-white nav-cta-btn">
                  <Link to="/get-involved#donate" data-testid="nav-donate-btn">
                    Donate Now
                  </Link>
                </Button>
              </div>

              <button
                className="lg:hidden premium-menu-btn"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle mobile menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay + Drawer */}
      <div className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />
      <aside className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="mobile-drawer-header">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="GLGIF Logo" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-semibold text-stone-900 leading-tight">GLGIF</p>
              <p className="text-xs text-stone-500">Saving Lives, Redirecting Destinies</p>
            </div>
          </div>
          <button
            className="premium-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mobile-drawer-content">
          <Link to="/" className="mobile-nav-link">Home</Link>

          {/* About (collapsible) */}
          <button
            className="mobile-section-trigger"
            onClick={() => setMobileAboutOpen((prev) => !prev)}
            aria-expanded={mobileAboutOpen}
          >
            <span>About</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className={`mobile-submenu ${mobileAboutOpen ? 'open' : ''}`}>
            {aboutLinks.map((link) => (
              <Link key={link.href} to={link.href} className="mobile-sub-link">
                {link.label}
              </Link>
            ))}
          </div>

          {/* What We Do (collapsible) */}
          <button
            className="mobile-section-trigger"
            onClick={() => setMobileWhatWeDoOpen((prev) => !prev)}
            aria-expanded={mobileWhatWeDoOpen}
          >
            <span>What We Do</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${mobileWhatWeDoOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className={`mobile-submenu ${mobileWhatWeDoOpen ? 'open' : ''}`}>
            {whatWeDoLinks.map((link) => (
              <Link key={link.href} to={link.href} className="mobile-sub-link">
                {link.label}
              </Link>
            ))}
          </div>

          <Link to="/get-involved" className="mobile-nav-link">Get Involved</Link>
          <Link to="/blog" className="mobile-nav-link">Blog</Link>
          <Link to="/events" className="mobile-nav-link">Events</Link>
          <Link to="/contact" className="mobile-nav-link">Contact</Link>
        </div>

        <div className="mobile-drawer-footer">
          <Button asChild className="w-full rounded-full py-6 text-white nav-cta-btn">
            <Link to="/get-involved#donate">Donate Now</Link>
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Navbar;